import React, { useRef, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import './RollingGallery.css';

const images = [
  '/images/IMG_0436-scaled.jpg',
  '/images/1.jpeg',
  '/images/2.jpeg',
  '/images/3.jpeg',
  '/images/4.jpeg',
  '/images/5.jpeg',
  '/images/6.jpeg',
  '/images/WhatsApp-Image-2025-06-14-at-08.53.43.jpeg',
  '/images/WhatsApp-Image-2025-06-14-at-06.08.34.jpeg',
  '/images/ball.jpeg',
  '/images/hike-2.jpeg',
  '/images/WhatsApp-Image-2025-06-14-at-05.48.08.jpeg',
  '/images/WhatsApp-Image-2025-06-14-at-05.30.34.jpeg',
  '/images/WhatsApp-Image-2025-06-14-at-06.02.42 (1).jpeg',
  '/images/IMG_0701-rotated-e1751307825464.jpg',
  '/images/IMG_5571-scaled-e1751307571827.jpg',
  '/images/IMG_0419 (1).jpg',
  '/images/IMG_5630-scaled-e1751306759551.jpg',
  '/images/WhatsApp-Image-2025-07-01-at-02.32.35-e1751317758236.jpeg',
  '/images/WhatsApp-Image-2025-07-01-at-02.32.36-3-e17513176473341.jpg',
  '/images/WhatsApp-Image-2025-06-14-at-09.16.56-1 (1).jpg',
];

const RollingGallery: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [modalImg, setModalImg] = useState<string | null>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const controls = useAnimation();
  const xRef = useRef(0);
  const [initialized, setInitialized] = useState(false);

  const shuffledImages = useMemo(() => {
    const copy = [...images];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, []);

  // Use 20 copies for a practically endless carousel
  const allImages = Array(20).fill(shuffledImages).flat();

  // Arrow navigation
  const cardWidth = 240 + 32; // card min-width + margin (from CSS)

  // True mathematical modulo
  function mod(n: number, m: number) {
    return ((n % m) + m) % m;
  }

  const correctInfinite = (x: number, trackWidth: number) => {
    // Always keep x in (-2 * trackWidth, 0)
    const range = 2 * trackWidth;
    return -range + mod(x + range, range);
  };

  const animateTo = (x: number, instant = false) => {
    if (instant) {
      controls.set({ x });
    } else {
      controls.start({ x, transition: { type: 'tween', ease: 'linear', duration: 0.3 } });
    }
  };

  const handleLeft = () => {
    if (!trackWidth) return;
    let next = xRef.current + cardWidth;
    next = correctInfinite(next, trackWidth);
    xRef.current = next;
    animateTo(next);
  };
  const handleRight = () => {
    if (!trackWidth) return;
    let next = xRef.current - cardWidth;
    next = correctInfinite(next, trackWidth);
    xRef.current = next;
    animateTo(next);
  };

  // Auto-scroll
  useEffect(() => {
    if (isPaused || !trackWidth || !initialized) return;
    let animationFrame: number;
    let lastTime = performance.now();
    const speed = 60; // px per second
    const animate = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      let next = xRef.current - speed * dt;
      next = correctInfinite(next, trackWidth);
      xRef.current = next;
      animateTo(next, true); // instant for auto-scroll
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isPaused, trackWidth, initialized]);

  // On mount, center to the middle set
  useEffect(() => {
    if (trackRef.current) {
      const singleSetWidth = trackRef.current.scrollWidth / 3;
      setTrackWidth(singleSetWidth);
      xRef.current = -singleSetWidth;
      animateTo(-singleSetWidth, true);
      setInitialized(true);
    }
    // eslint-disable-next-line
  }, []);

  // Modal close handler
  const handleModalClose = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setModalImg(null);
  };

  return (
    <div className="rolling-gallery-container">
      <button className="carousel-arrow left" onClick={handleLeft} aria-label="Previous">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0a174e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <motion.div
        className="rolling-gallery-track"
        ref={trackRef}
        animate={controls}
        style={{ display: 'flex' }}
      >
        {allImages.map((img, i) => (
          <motion.div
            className="rolling-gallery-card"
            key={i}
            onMouseEnter={() => { setIsPaused(true); setHoveredIdx(i); }}
            onMouseLeave={() => { setIsPaused(false); setHoveredIdx(null); }}
            onClick={() => setModalImg(img)}
            animate={hoveredIdx === i ? { scale: 1.15, zIndex: 2 } : { scale: 1, zIndex: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            style={{ position: 'relative', cursor: 'pointer' }}
          >
            <img src={img} alt={`gallery-${i}`} draggable={false} />
          </motion.div>
        ))}
      </motion.div>
      <button className="carousel-arrow right" onClick={handleRight} aria-label="Next">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0a174e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <AnimatePresence>
        {modalImg && (
          <motion.div
            className="gallery-modal-backdrop"
            onClick={handleModalClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          >
            <motion.img
              src={modalImg + '?w=1000&q=80'}
              alt="modal"
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.7, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
              style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', background: '#fff' }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RollingGallery;
